import * as React from "react";
import { Link } from "react-router-dom";
import cx from "classnames";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";

interface AccessReportsAdminWizardStepProps {
  children?: React.ReactNode;
  currentWizardStep: number;
  nextPath?: string;
  nextPathDisabled?: boolean;
  nextPathDisabledTooltipText?: string;
  onSaveProductClick?: () => void;
  previousPath?: string;
  setWizardStep: (page: number) => void;
  wizardSteps: string[];
}

const AccessReportsAdminWizardStep: React.FC<AccessReportsAdminWizardStepProps> = (props: AccessReportsAdminWizardStepProps) => {
  const wizardBodyRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    focusFirstInteractiveElement(wizardBodyRef);
  }, []);

  React.useEffect(() => {
    // something's weird with the third step, page options,
    // that causes the focus to start in the middle of the page
    // so I'd rather leave this parameter blank and let it go to the top of the page
    focusFirstInteractiveElement();
  }, [props.currentWizardStep]);

  const hasChildren = React.Children.count(props.children) > 0;

  return (
    <Page className="bg-white" documentTitle="Access Reports Admin Wizard">
      <PageInner>
        <PageSection>
          <div className="row">
            <div className="col-12">
              <h2 className="mt-3 mb-3 header-l demibold">
                Add Product
              </h2>
            </div>
          </div>

          <div className="row">
            <div className="col-10">
              <WizardSteps
                currentStep={props.currentWizardStep}
                setStep={props.setWizardStep}
                steps={props.wizardSteps}
              />
            </div>

            <div className="col-12">
              <hr />
            </div>
          </div>

          <div ref={wizardBodyRef}>
            {hasChildren && props.children}
          </div>

          <div className="row">
            <div className="col-12">
              <hr />
            </div>
          </div>
        </PageSection>
      </PageInner>
      <WizardNavigationBar
        leftSide={
          <Link
            className="qes-button qes-button-secondary danger"
            id="access-reports-admin__wizard-step--cancel"
            to={{
              pathname: `/access-reports/admin/dashboard`,
            }}
            type="button"
          >
            Cancel
          </Link>
        }
        rightSide={
          <div>
            {props.previousPath &&
              <Link
                id="access-reports-admin__wizard-step--previous"
                className="qes-button qes-button-secondary"
                to={{
                  pathname: `${props.previousPath}`,
                }}
                type="button"
              >
                Previous
              </Link>
            }

            {props.nextPath && (
              <TooltipWrapper
                text={props.nextPathDisabled && props.nextPathDisabledTooltipText}
                position="top"
              >
                <Link
                  className={cx({
                    "disabled": props.nextPathDisabled,
                    "ms-3": true,
                    "qes-button": true,
                    "qes-button-primary": true,
                  })}
                  aria-disabled={props.nextPathDisabled}
                  id="access-reports-admin__wizard-step--next"
                  onClick={(e) => e.currentTarget.blur()}
                  to={{
                    pathname: `${props.nextPath}`,
                  }}
                  type="button"
                >
                  Next
                </Link>
              </TooltipWrapper>
            )}

            {props.onSaveProductClick && (
              <TooltipWrapper
                text={props.nextPathDisabled && props.nextPathDisabledTooltipText}
                position="top"
              >
                <QESButton
                  className="cursor-pointer ms-3"
                  disabled={props.nextPathDisabled}
                  onClick={props.onSaveProductClick}
                  qesTestId="access-reports-admin__wizard-step--submit"
                  qesType="primary"
                >
                  Submit
                </QESButton>
              </TooltipWrapper>
            )}
          </div>
        }
      />
    </Page>
  );
}

export { AccessReportsAdminWizardStep };
